import CharacterCount from '@boViews/shared/CharacterCount';
import { languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { DURATION_MAX_MINUTES, durationRules, toDurationPayload } from '@boViews/shared/testDuration';
import TestStatusBadge from '@boViews/shared/TestStatusBadge';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import FormGroup from '@sharedComponents/FormGroup';
import InputText from '@sharedComponents/InputText';
import InputTextArea from '@sharedComponents/InputTextArea';
import Select from '@sharedComponents/Select';
import ToggleSwitch from '@sharedComponents/ToggleSwitch';
import Typography from '@sharedComponents/Typography';
import { REFERENCE_LANGUAGE } from '@src/i18n';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const REFERENCE_MAX_LENGTH = 50;
// Same rule as the backend (CreateTestRequestDto::REFERENCE_PATTERN): the reference goes into URLs.
const REFERENCE_PATTERN = /^[A-Za-z0-9_.-]+$/;
// Indicative only (the counters never block): the title and the description have no backend limit.
const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;
const DEFAULT_LEVEL_ID = 1;
const DEFAULT_STANDALONE_STATUS = 'DRAFT';

const noSubmission = async () => {};

/**
 * Side panel creating a test from its reference, its French title and description, its language, its level, its
 * optional duration in minutes and its pedagogical flag. Its status is shown as online, frozen: a test added to a
 * collection is online. With `standalone` (a test created outside a collection), the status is chosen instead (a
 * draft by default) and sent with the test, and the button reads "Create". The title and the description are sent as French i18n JSON; their
 * counters are indicative, only the reference is limited. `initialReference` fills the reference field, `languagesOptions`
 * are the catalogue languages (their id labelled by their code) and `levelsOptions` the levels. `onSubmit(payload)` resolves once the test is created; a
 * rejection with a 409 status marks the reference as taken, any other rejection is reported by the caller.
 */
const TestCreatePanel = ({
    initialReference = '',
    languagesOptions = [],
    levelsOptions = [],
    onSubmit = noSubmission,
    standalone = false,
} = {}) => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const languageChoices = languagesOptions.map((option) => ({ ...option, label: languageName(option.label, locale) }));
    const defaultLanguage = languagesOptions.find((option) => option.label === REFERENCE_LANGUAGE) ?? languagesOptions[0];
    const statusChoices = [
        { value: 'DRAFT', label: t('Draft') },
        { value: 'ONLINE', label: t('Online') },
        { value: 'ARCHIVED', label: t('Archived') },
    ];
    const defaultLevel = levelsOptions.find((option) => option.value === DEFAULT_LEVEL_ID) ?? levelsOptions[0];

    const {
        register,
        handleSubmit,
        control,
        setError,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            reference: initialReference,
            title: '',
            description: '',
            languageId: defaultLanguage?.value ?? null,
            pedagogical: false,
            levelId: defaultLevel?.value ?? null,
            duration: '',
            status: DEFAULT_STANDALONE_STATUS,
        },
    });
    const [title, description] = useWatch({ control, name: ['title', 'description'] });

    const required = (message) => (value) => value.trim() !== '' || message;
    const tooLong = (max) => ({ value: max, message: t('Maximum {{max}} characters', { max }) });

    const submit = async (data) => {
        const trimmedDescription = data.description.trim();
        try {
            await onSubmit({
                reference: data.reference.trim(),
                title: JSON.stringify({ fr: data.title.trim() }),
                description: trimmedDescription ? JSON.stringify({ fr: trimmedDescription }) : null,
                languageId: data.languageId,
                levelId: data.levelId,
                duration: toDurationPayload(data.duration),
                pedagogical: data.pedagogical,
                ...(standalone && { status: data.status }),
            });
        } catch (err) {
            // Any other error is already surfaced as a toast by the caller.
            if (err?.status === 409) {
                setError('reference', { type: 'manual', message: t('This test reference already exists') });
            }
        }
    };

    return (
        <FlexBox flexDirection="column" gap="m">
            <FormGroup label={t('Reference')} errorMsg={errors.reference?.message}>
                <InputText
                    aria-label={t('Reference')}
                    isError={Boolean(errors.reference)}
                    {...register('reference', {
                        validate: {
                            required: required(t('Reference is required')),
                            characters: (value) =>
                                value.trim() === '' ||
                                REFERENCE_PATTERN.test(value.trim()) ||
                                t('Only letters, digits, "_", "." and "-" are allowed'),
                        },
                        maxLength: tooLong(REFERENCE_MAX_LENGTH),
                    })}
                />
            </FormGroup>
            <FormGroup label={t('Title (French)')} errorMsg={errors.title?.message}>
                <InputText
                    aria-label={t('Title (French)')}
                    isError={Boolean(errors.title)}
                    {...register('title', { validate: required(t('The French title is required')) })}
                />
                <CharacterCount length={title.length} max={TITLE_MAX_LENGTH} />
            </FormGroup>
            <FormGroup label={t('Description (French)')}>
                <InputTextArea aria-label={t('Description (French)')} {...register('description')} />
                <CharacterCount length={description.length} max={DESCRIPTION_MAX_LENGTH} />
            </FormGroup>
            <FormGroup label={t('Language')}>
                <Select
                    control={control}
                    register={{ name: 'languageId' }}
                    options={languageChoices}
                    isSearchable={false}
                    menuPlacement="auto"
                    aria-label={t('Language')}
                />
            </FormGroup>
            <FormGroup label={t('Level')}>
                <Select
                    control={control}
                    register={{ name: 'levelId' }}
                    options={levelsOptions}
                    isSearchable={false}
                    menuPlacement="auto"
                    aria-label={t('Level')}
                />
            </FormGroup>
            <FormGroup label={t('Duration (minutes)')} errorMsg={errors.duration?.message}>
                {/* A text input: a number input would turn letters into an empty value, sent as no duration. */}
                <InputText
                    inputMode="numeric"
                    aria-label={t('Duration (minutes)')}
                    isError={Boolean(errors.duration)}
                    {...register(
                        'duration',
                        durationRules(
                            t('The duration must be a whole number of minutes between 1 and {{max}}', { max: DURATION_MAX_MINUTES }),
                        ),
                    )}
                />
            </FormGroup>
            <FlexBox alignItems="center" gap="s">
                <ToggleSwitch register={register('pedagogical')} ariaLabel={t('Pedagogical')} />
                <Typography>{t('Pedagogical')}</Typography>
            </FlexBox>
            <FormGroup label={t('Status')}>
                {standalone ? (
                    <Select
                        control={control}
                        register={{ name: 'status' }}
                        options={statusChoices}
                        isSearchable={false}
                        menuPlacement="auto"
                        aria-label={t('Status')}
                    />
                ) : (
                    <FlexBox alignItems="center" gap="s">
                        <TestStatusBadge status="ONLINE" />
                        <Typography fontSize="s" color="neutral-secondary">
                            {t('A test added to a collection is online')}
                        </Typography>
                    </FlexBox>
                )}
            </FormGroup>
            <FlexBox justifyContent="flex-end">
                <Button
                    label={standalone ? t('Create') : t('Create and add')}
                    action={handleSubmit(submit)}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                />
            </FlexBox>
        </FlexBox>
    );
};

export default TestCreatePanel;
